import { createDomElementHandler } from "@/scripts/utils/dom.utils";
import { resolveBreakpointValue } from "@/scripts/utils/breakpoints.util";
import "./abPostCard.style.scss";

// Module-level resize listener shared across all instances
const _resizeListeners = new Set<() => void>();
let _resizeListenerRegistered = false;

function _ensureResizeListener() {
  if (_resizeListenerRegistered) return;
  _resizeListenerRegistered = true;
  window.addEventListener("resize", () => {
    _resizeListeners.forEach(fn => fn());
  });
}

export class AbPostCardElement extends HTMLElement {

  #initialized = false;
  #cardEl: HTMLAnchorElement;
  #onResize: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return ["data-title", "data-intro", "data-image", "data-link", "data-layout", "data-card-breakpoints"];
  }

  connectedCallback() {
    this.#render();
    this.#onResize = () => this.#updateLayout();
    _ensureResizeListener();
    _resizeListeners.add(this.#onResize);
  }

  disconnectedCallback() {
    if (this.#onResize) {
      _resizeListeners.delete(this.#onResize);
      this.#onResize = null;
    }
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#render();
  }

  // -------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------

  get #title()  { return this.getAttribute("data-title")  ?? ""; }
  get #intro()  { return this.getAttribute("data-intro")  ?? ""; }
  get #image()  { return this.getAttribute("data-image")  ?? ""; }
  get #link()   { return this.getAttribute("data-link")   ?? "#"; }
  get #layout() { return this.getAttribute("data-layout") === "horizontal" ? "horizontal" : "vertical"; }

  /**
   * Parses data-card-breakpoints and returns the layout for the current viewport width.
   * Breakpoints format: { "0": "vertical", "768": "horizontal" }
   * The largest min-width key that is <= window.innerWidth wins.
   * Falls back to data-layout if no breakpoints are defined or none match.
   */
  #resolveLayout(): "horizontal" | "vertical" {
    const raw = this.getAttribute("data-card-breakpoints");
    if (!raw) return this.#layout;

    let parsed: Record<string, string>;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return this.#layout;
    }

    const breakpoints = Object.keys(parsed)
      .map(Number)
      .filter(k => !isNaN(k))
      .map((bp): readonly [number, string] => [bp, parsed[String(bp)]]);
    const resolved = resolveBreakpointValue(breakpoints, window.innerWidth);

    if (resolved === "horizontal" || resolved === "vertical") return resolved;
    return this.#layout;
  }

  #render() {
    if (!this.#initialized) {
      this.#buildDOM();
      this.#initialized = true;
    }
    this.#update();
  }

  #buildDOM() {
    this.#cardEl = createDomElementHandler("a", ["ab-post-card"]) as HTMLAnchorElement;

    const imageWrapper = createDomElementHandler("div", "ab-post-card__image-wrapper");
    const imageEl     = createDomElementHandler("img", "ab-post-card__image");
    imageWrapper.appendChild(imageEl);

    const body      = createDomElementHandler("div", "ab-post-card__body");
    const titleEl   = createDomElementHandler("h3", "ab-post-card__title");
    const introEl   = createDomElementHandler("p", "ab-post-card__intro");
    body.replaceChildren(titleEl, introEl);

    this.#cardEl.replaceChildren(imageWrapper, body);
    this.replaceChildren(this.#cardEl);
  }

  #updateLayout() {
    const layout = this.#resolveLayout();
    this.#cardEl.classList.toggle("ab-post-card--horizontal", layout === "horizontal");
    this.#cardEl.classList.toggle("ab-post-card--vertical",   layout === "vertical");
  }

  #update() {
    // Link
    this.#cardEl.href = this.#link;

    // Layout
    this.#updateLayout();

    // Image
    const imageEl = this.#cardEl.querySelector<HTMLImageElement>(".ab-post-card__image");
    if (imageEl) {
      imageEl.src = this.#image;
      imageEl.alt = this.#title;
    }

    // Text
    const titleEl = this.#cardEl.querySelector(".ab-post-card__title");
    if (titleEl) titleEl.textContent = this.#title;

    const introEl = this.#cardEl.querySelector(".ab-post-card__intro");
    if (introEl) introEl.textContent = this.#intro;
  }

}

export function defineAbPostCardElement() {
  const TAG_NAME = "ab-post-card";
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, AbPostCardElement);
}
